import styled from '@emotion/styled';
import {
  BadgeRounded,
  CheckCircleRounded,
  CloseRounded,
  EmailRounded,
  KeyboardArrowDownRounded,
  LogoutRounded,
  PersonRounded,
  PhoneRounded,
} from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { useKaraokeAuth } from '~/modules/Auth/KaraokeAuthContext';
import { useAuth } from '../AuthContext';
import { WalkyUser } from '../types';

export default function UserAccountMenu() {
  const { user, logout, isAuthenticated } = useAuth();
  const karaokeAuth = useKaraokeAuth();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside or escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const currentUser: WalkyUser | null =
    user ||
    (isAuthenticated ? { name: 'Usuario WalkyCam' } : null) ||
    (karaokeAuth?.status === 'authenticated' && karaokeAuth.user && karaokeAuth.user.type === 'user'
      ? {
          name: karaokeAuth.user.name ?? 'Usuario',
          email: karaokeAuth.user.email ?? undefined,
          role: 'Miembro WalkyCam',
        }
      : null);

  if (!currentUser) {
    return (
      <LoginTriggerButton
        type="button"
        onClick={() => {
          window.dispatchEvent(new CustomEvent('karaoke:open-auth', { detail: 'login' }));
        }}
        aria-label="Iniciar sesión">
        <PersonRounded fontSize="small" />
        <span>Iniciar sesión</span>
      </LoginTriggerButton>
    );
  }

  const fullName = [currentUser.name, currentUser.last_name].filter(Boolean).join(' ').trim();
  const displayName = fullName || currentUser.user_name || currentUser.name || currentUser.email || 'Usuario';
  const avatarUrl: string | undefined = currentUser.url_profile_picture || currentUser.profile_picture;
  const username = currentUser.user_name ? `@${currentUser.user_name.replace(/^@/, '')}` : null;
  const phoneNumber: string | undefined = currentUser.phone_number;

  const handleLogout = () => {
    setIsOpen(false);
    logout();
    if (karaokeAuth?.logout) {
      void karaokeAuth.logout();
    }
  };

  return (
    <Container ref={containerRef}>
      <TriggerButton
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        title={displayName}>
        <AvatarThumb>
          {avatarUrl ? (
            <AvatarThumbImg src={avatarUrl} alt={displayName} />
          ) : (
            <AvatarThumbFallback>
              <PersonRounded fontSize="inherit" />
            </AvatarThumbFallback>
          )}
        </AvatarThumb>
        <TriggerName>{displayName}</TriggerName>
        <ArrowIcon isOpen={isOpen}>
          <KeyboardArrowDownRounded fontSize="inherit" />
        </ArrowIcon>
      </TriggerButton>

      {isOpen && (
        <DropdownPanel role="dialog" aria-label="Información de la cuenta">
          <PanelHeader>
            <PanelCloseButton type="button" onClick={() => setIsOpen(false)} aria-label="Cerrar panel">
              <CloseRounded fontSize="small" />
            </PanelCloseButton>

            <LargeAvatarWrap>
              {avatarUrl ? (
                <LargeAvatarImg src={avatarUrl} alt={displayName} />
              ) : (
                <LargeAvatarFallback>
                  <PersonRounded sx={{ fontSize: 38 }} />
                </LargeAvatarFallback>
              )}
              {currentUser.is_verified && (
                <VerifiedDot title="Cuenta verificada">
                  <CheckCircleRounded sx={{ fontSize: 18 }} />
                </VerifiedDot>
              )}
            </LargeAvatarWrap>

            <ProfileTitle>{displayName}</ProfileTitle>
            {username && <ProfileUsername>{username}</ProfileUsername>}

            {currentUser.is_verified ? (
              <BadgeTag verified>
                <CheckCircleRounded sx={{ fontSize: 13 }} />
                <span>Cuenta verificada</span>
              </BadgeTag>
            ) : (
              <BadgeTag>
                <span>Cuenta WalkyCam</span>
              </BadgeTag>
            )}
          </PanelHeader>

          <PanelBody>
            <SectionTitle>Detalles de la cuenta</SectionTitle>

            {currentUser.email && (
              <InfoItem>
                <InfoIconWrap>
                  <EmailRounded fontSize="small" />
                </InfoIconWrap>
                <InfoContent>
                  <InfoLabel>Correo electrónico</InfoLabel>
                  <InfoValue title={currentUser.email}>{currentUser.email}</InfoValue>
                </InfoContent>
              </InfoItem>
            )}

            {currentUser.role && (
              <InfoItem>
                <InfoIconWrap>
                  <BadgeRounded fontSize="small" />
                </InfoIconWrap>
                <InfoContent>
                  <InfoLabel>Rol / Permiso</InfoLabel>
                  <InfoValue>{currentUser.role}</InfoValue>
                </InfoContent>
              </InfoItem>
            )}

            {phoneNumber && (
              <InfoItem>
                <InfoIconWrap>
                  <PhoneRounded fontSize="small" />
                </InfoIconWrap>
                <InfoContent>
                  <InfoLabel>Teléfono</InfoLabel>
                  <InfoValue>{phoneNumber}</InfoValue>
                </InfoContent>
              </InfoItem>
            )}
          </PanelBody>

          <PanelFooter>
            <LogoutButton type="button" onClick={handleLogout}>
              <LogoutRounded fontSize="small" />
              <span>Cerrar sesión</span>
            </LogoutButton>
          </PanelFooter>
        </DropdownPanel>
      )}
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  display: inline-block;

  @media (max-width: 640px) {
    width: 100%;
    min-width: 0;
  }
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--walky-surface);
  border: 1px solid var(--walky-border);
  padding: 4px 10px 4px 5px;
  border-radius: 9999px;
  max-width: 240px;
  box-shadow: var(--walky-shadow);
  cursor: pointer;
  outline: none;
  font-family: inherit;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    transform 0.1s ease;

  &:hover {
    border-color: var(--walky-orange);
    background: var(--walky-surface-raised);
  }

  &:focus-visible {
    border-color: var(--walky-orange);
    box-shadow: 0 0 0 3px var(--walky-orange-soft);
  }

  @media (max-width: 520px) {
    max-width: 150px;
    padding: 3px 8px 3px 4px;
  }

  @media (max-width: 640px) {
    width: 100%;
    height: 38px;
    max-width: none;
    justify-content: center;
    padding: 0;
    border-radius: 8px;
  }
`;

const AvatarThumb = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 640px) {
    width: 26px;
    height: 26px;
  }
`;

const AvatarThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarThumbFallback = styled.div`
  width: 100%;
  height: 100%;
  background: var(--walky-orange-soft);
  color: var(--walky-orange);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const TriggerName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: var(--walky-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 640px) {
    display: none;
  }
`;

const ArrowIcon = styled.span<{ isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: var(--walky-muted);
  transition:
    transform 0.2s ease,
    color 0.15s ease;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};

  ${TriggerButton}:hover & {
    color: var(--walky-orange);
  }

  @media (max-width: 640px) {
    display: none;
  }
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 290px;
  max-width: calc(100vw - 28px);
  background: var(--walky-surface);
  border: 1px solid var(--walky-border);
  border-radius: 16px;
  box-shadow: var(--walky-shadow-strong);
  z-index: 1200;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: panelSlideDown 0.16s ease-out;

  @keyframes panelSlideDown {
    from {
      opacity: 0;
      transform: translateY(-6px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 640px) {
    position: fixed;
    top: calc(var(--karaoke-top-bar-height, 104px) + 8px);
    right: 10px;
    left: 10px;
    width: auto;
    max-width: none;
    max-height: calc(100dvh - var(--karaoke-top-bar-height, 104px) - 20px);
    overflow-y: auto;
  }
`;

const PanelHeader = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 22px 16px 14px;
  background: linear-gradient(180deg, var(--walky-orange-soft) 0%, var(--walky-surface) 100%);
  border-bottom: 1px solid var(--walky-border);
`;

const PanelCloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: var(--walky-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--walky-ink);
  }
`;

const LargeAvatarWrap = styled.div`
  position: relative;
  width: 68px;
  height: 68px;
  border-radius: 50%;
  margin-bottom: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const LargeAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--walky-surface);
`;

const LargeAvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--walky-orange-soft);
  color: var(--walky-orange);
  border: 3px solid var(--walky-surface);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VerifiedDot = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  color: #10b981;
  background: var(--walky-surface);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--walky-ink);
  text-align: center;
  word-break: break-word;
`;

const ProfileUsername = styled.span`
  font-size: 12px;
  color: var(--walky-muted);
  margin-top: 2px;
  font-weight: 500;
`;

const BadgeTag = styled.div<{ verified?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 3px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ verified }) => (verified ? '#ecfdf5' : 'var(--walky-neutral-soft)')};
  color: ${({ verified }) => (verified ? '#059669' : 'var(--walky-muted)')};
  border: 1px solid ${({ verified }) => (verified ? '#a7f3d0' : 'var(--walky-border)')};
`;

const PanelBody = styled.div`
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 4px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  color: var(--walky-muted);
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  background: var(--walky-neutral-soft);
`;

const InfoIconWrap = styled.div`
  color: var(--walky-orange);
  display: flex;
  align-items: center;
  margin-top: 2px;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const InfoLabel = styled.span`
  font-size: 10px;
  color: var(--walky-muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const InfoValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--walky-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PanelFooter = styled.div`
  padding: 10px 14px 14px;
  border-top: 1px solid var(--walky-border);
`;

const LogoutButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--walky-surface-raised);
  border: 1px solid var(--walky-border);
  color: var(--walky-ink);
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: var(--walky-error-bg);
    border-color: var(--walky-error-border);
    color: var(--walky-error-text);
  }
`;

const LoginTriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 16px;
  border-radius: 9999px;
  border: 1px solid rgba(244, 90, 10, 0.45);
  background: linear-gradient(135deg, var(--walky-orange), #ff7a1a);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(244, 90, 10, 0.25);
  transition: all 180ms ease;

  &:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(244, 90, 10, 0.35);
  }

  svg {
    font-size: 18px;
  }

  @media (max-width: 640px) {
    width: 100%;
    height: 38px;
    justify-content: center;
    padding: 0;
    border-radius: 8px;

    span {
      display: none;
    }
  }
`;
